import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { explainTaxCode, type TaxCodeExplanation } from '@/api/taxCodeApi';
import { PageLayout } from '@/components';
import { Alert, Button } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export function TaxCodeDetailPage(): React.JSX.Element {
  const { code } = useParams<{ code: string }>();
  const [explanation, setExplanation] = useState<TaxCodeExplanation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;

    const fetchExplanation = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      setExplanation(null); // Clear stale content when code changes

      try {
        const data = await explainTaxCode(code);
        setExplanation(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tax code explanation');
      } finally {
        setLoading(false);
      }
    };

    fetchExplanation();
  }, [code]);

  // Dynamic SEO meta tags based on tax code and explanation
  const metaTitle = code ? `${code.toUpperCase()} Tax Code Explained` : 'Tax Code Explained';
  const metaDescription = explanation?.summary
    ? `${explanation.summary} Learn what the ${code?.toUpperCase()} tax code means for your UK tax situation.`
    : code
      ? `Understand what the UK tax code ${code.toUpperCase()} means for your personal allowance and tax payments.`
      : 'Understand your UK tax code and what it means for your tax situation.';

  useDocumentMeta({
    title: metaTitle,
    description: metaDescription,
  });

  const getRegionBadge = (region: string | null): React.JSX.Element | null => {
    if (!region) return null;

    const regionLabels: Record<string, string> = {
      england: 'England/NI',
      scotland: 'Scotland',
      wales: 'Wales',
    };

    const regionColors: Record<string, 'default' | 'primary' | 'warning'> = {
      england: 'default',
      scotland: 'primary',
      wales: 'warning',
    };

    return (
      <Badge variant={regionColors[region] ?? 'default'}>
        {regionLabels[region] ?? region}
      </Badge>
    );
  };

  return (
    <PageLayout maxWidth="lg">
      <div className="space-y-6">
        {/* Back Link */}
        <Link
          to="/tax-code"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Check another code
        </Link>

        {/* Loading State */}
        {loading && (
          <Card padding="lg" className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-32 mx-auto mb-4" />
              <div className="h-4 bg-gray-200 rounded w-64 mx-auto" />
            </div>
          </Card>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert variant="error" title="Error">
            {error}
          </Alert>
        )}

        {/* Explanation */}
        {explanation && !loading && (
          <>
            {/* Header Card */}
            <Card padding="lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-mono font-bold text-gray-900">
                      {code?.toUpperCase()}
                    </h1>
                    {explanation.is_valid ? (
                      <Badge variant="success">Valid</Badge>
                    ) : (
                      <Badge variant="error">Invalid</Badge>
                    )}
                    {getRegionBadge(explanation.region)}
                  </div>
                  <p className="text-lg text-gray-600">{explanation.summary}</p>
                </div>
                {explanation.personal_allowance && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Personal allowance</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {explanation.personal_allowance}
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Invalid Code Error */}
            {!explanation.is_valid && explanation.error && (
              <Alert variant="error" title="Invalid Tax Code">
                {explanation.error}
              </Alert>
            )}

            {/* Warnings */}
            {explanation.warnings.length > 0 && (
              <div className="space-y-3">
                {explanation.warnings.map((warning) => (
                  <Alert key={warning} variant="warning">
                    {warning}
                  </Alert>
                ))}
              </div>
            )}

            {/* Details */}
            {explanation.details.length > 0 && (
              <Card padding="md">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  What this code means
                </h2>
                <ul className="space-y-3">
                  {explanation.details.map((detail) => (
                    <li key={detail} className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 items-center">
              <Link to="/tax-code">
                <Button variant="outline">Check another code</Button>
              </Link>
              <div className="text-center mt-4">
                <p className="text-gray-600 mb-2">Check what tax code you should be on</p>
                <Link to="/tax-code">
                  <Button variant="primary">Check</Button>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}

export default TaxCodeDetailPage;
